// ═══════════════════════════════════════════════════════════════════════════════
// Review-step row builders for pickup / medication task group wizards
// ═══════════════════════════════════════════════════════════════════════════════

import { RECURRENCE_TYPE } from '@fdh/shared';
import type { InventoryWizardReviewRow } from './InventoryWizardReviewStep';
import type { TaskGroupScheduleState } from './TaskGroupScheduleFields';

const DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
] as const;

const reviewRow = (
  id: string,
  labelKey: string,
  valueText: string,
  isEmpty: boolean,
  t: (key: string) => string,
  preserveWhitespace?: boolean,
): InventoryWizardReviewRow => ({
  id,
  label: t(labelKey),
  valueText,
  valueVariant: isEmpty ? 'empty' : 'default',
  preserveWhitespace,
});

export const buildTaskGroupRecurrenceSummaryText = (
  t: (key: string) => string,
  schedule: TaskGroupScheduleState,
): string => {
  if (schedule.recurrence === RECURRENCE_TYPE.NONE) {
    return `${t('task.recurrence.none')} · ${schedule.occurrenceDate}`;
  }
  if (schedule.recurrence === RECURRENCE_TYPE.MONTHLY) {
    const endPart =
      schedule.endDate !== '' ? ` · ${t('task.form.endDateLabel')}: ${schedule.endDate}` : '';
    return `${t('task.recurrence.monthly')} · ${t('task.form.startDateLabel')}: ${schedule.startDate}${endPart}`;
  }
  const head =
    schedule.recurrence === RECURRENCE_TYPE.DAILY
      ? t('task.recurrence.daily')
      : schedule.recurrence === RECURRENCE_TYPE.WEEKLY
        ? t('task.recurrence.weekly')
        : t('task.recurrence.biweekly');
  const daysPart =
    schedule.recurrence === RECURRENCE_TYPE.WEEKLY ||
    schedule.recurrence === RECURRENCE_TYPE.BIWEEKLY
      ? schedule.recurrenceDays.length > 0
        ? ` · ${[...schedule.recurrenceDays]
            .sort((a, b) => a - b)
            .map((day) => t(DAY_KEYS[day] ?? DAY_KEYS[0]))
            .join(', ')}`
        : ''
      : '';
  const endPart =
    schedule.endDate !== '' ? ` · ${t('task.form.endDateLabel')}: ${schedule.endDate}` : '';
  return `${head}${daysPart} · ${t('task.form.startDateLabel')}: ${schedule.startDate}${endPart}`;
};

export const buildTaskGroupExclusionSummaryText = (
  t: (key: string) => string,
  schedule: TaskGroupScheduleState,
): string => {
  const none = t('task.group.wizard.summaryNone');
  if (schedule.recurrence === RECURRENCE_TYPE.NONE) {
    return none;
  }
  const ranges = schedule.exclusionRanges.filter((r) => r.startDate !== '' && r.endDate !== '');
  if (ranges.length === 0) {
    return none;
  }
  return ranges.map((r) => `${r.startDate} — ${r.endDate}`).join('\n');
};

type PickupDropoffReviewInput = {
  t: (key: string) => string;
  personName: string;
  location: string;
  description: string;
  pickupEnabled: boolean;
  dropoffEnabled: boolean;
  resolvedTitlePickup: string;
  resolvedTitleDropoff: string;
  pickupTime: string;
  dropoffTime: string;
  schedule: TaskGroupScheduleState;
};

export const buildPickupDropoffReviewRows = ({
  t,
  personName,
  location,
  description,
  pickupEnabled,
  dropoffEnabled,
  resolvedTitlePickup,
  resolvedTitleDropoff,
  pickupTime,
  dropoffTime,
  schedule,
}: PickupDropoffReviewInput): InventoryWizardReviewRow[] => {
  const none = t('task.group.wizard.summaryNone');
  const recurrenceSummaryText = buildTaskGroupRecurrenceSummaryText(t, schedule);
  const exclusionSummaryText = buildTaskGroupExclusionSummaryText(t, schedule);
  const descTrimmed = description.trim();
  const personTrimmed = personName.trim();
  const locationTrimmed = location.trim();
  const rows: InventoryWizardReviewRow[] = [
    reviewRow(
      'rev-person',
      'task.group.wizard.summaryPerson',
      personTrimmed !== '' ? personTrimmed : none,
      personTrimmed === '',
      t,
    ),
    reviewRow(
      'rev-location',
      'task.group.wizard.summaryLocation',
      locationTrimmed !== '' ? locationTrimmed : none,
      locationTrimmed === '',
      t,
    ),
  ];
  if (dropoffEnabled) {
    rows.push(
      reviewRow(
        'rev-dropoff-title',
        'task.pickup.reviewDropoffTitle',
        resolvedTitleDropoff,
        resolvedTitleDropoff === '',
        t,
      ),
      reviewRow(
        'rev-dropoff-time',
        'task.pickup.dropoffTimeLabel',
        dropoffTime.trim() !== '' ? dropoffTime : none,
        dropoffTime.trim() === '',
        t,
      ),
    );
  }
  if (pickupEnabled) {
    rows.push(
      reviewRow(
        'rev-pickup-title',
        'task.pickup.reviewPickupTitle',
        resolvedTitlePickup,
        resolvedTitlePickup === '',
        t,
      ),
      reviewRow(
        'rev-pickup-time',
        'task.pickup.pickupTimeLabel',
        pickupTime.trim() !== '' ? pickupTime : none,
        pickupTime.trim() === '',
        t,
      ),
    );
  }
  rows.push(
    reviewRow(
      'rev-desc',
      'task.inventory.wizard.summaryDesc',
      descTrimmed !== '' ? descTrimmed : none,
      descTrimmed === '',
      t,
      true,
    ),
    reviewRow('rev-schedule', 'task.inventory.wizard.summaryRecurrence', recurrenceSummaryText, false, t),
    reviewRow(
      'rev-exclusion',
      'task.group.wizard.summaryExclusion',
      exclusionSummaryText,
      exclusionSummaryText === none,
      t,
      true,
    ),
    reviewRow(
      'rev-require-photo',
      'task.group.wizard.summaryRequirePhoto',
      schedule.requirePhoto
        ? t('task.group.wizard.summaryYes')
        : t('task.group.wizard.summaryNo'),
      false,
      t,
    ),
    reviewRow(
      'rev-notify',
      'task.inventory.wizard.summaryNotify',
      schedule.notifyEmployer
        ? t('task.inventory.wizard.summaryNotifyOn')
        : t('task.inventory.wizard.summaryNotifyOff'),
      false,
      t,
    ),
    reviewRow(
      'rev-remind-helper',
      'task.form.remindHelperBeforeDueLabel',
      schedule.remindHelperBeforeDue
        ? t('task.group.wizard.summaryYes')
        : t('task.group.wizard.summaryNo'),
      false,
      t,
    ),
  );
  return rows;
};

type MedicationReminderReviewInput = {
  t: (key: string) => string;
  personName: string;
  description: string;
  resolvedTitle: string;
  medicationTimes: string[];
  schedule: TaskGroupScheduleState;
};

export const buildMedicationReminderReviewRows = ({
  t,
  personName,
  description,
  resolvedTitle,
  medicationTimes,
  schedule,
}: MedicationReminderReviewInput): InventoryWizardReviewRow[] => {
  const none = t('task.group.wizard.summaryNone');
  const recurrenceSummaryText = buildTaskGroupRecurrenceSummaryText(t, schedule);
  const exclusionSummaryText = buildTaskGroupExclusionSummaryText(t, schedule);
  const descTrimmed = description.trim();
  const personTrimmed = personName.trim();
  const timesJoined = medicationTimes.filter((time) => time.trim() !== '').join(', ');

  return [
    reviewRow(
      'rev-person',
      'task.group.wizard.summaryPerson',
      personTrimmed !== '' ? personTrimmed : none,
      personTrimmed === '',
      t,
    ),
    reviewRow(
      'rev-title',
      'task.medication.reviewTitle',
      resolvedTitle !== '' ? resolvedTitle : none,
      resolvedTitle === '',
      t,
    ),
    reviewRow(
      'rev-times',
      'task.group.wizard.summaryMedicationTimes',
      timesJoined !== '' ? timesJoined : none,
      timesJoined === '',
      t,
    ),
    reviewRow(
      'rev-desc',
      'task.inventory.wizard.summaryDesc',
      descTrimmed !== '' ? descTrimmed : none,
      descTrimmed === '',
      t,
      true,
    ),
    reviewRow('rev-schedule', 'task.inventory.wizard.summaryRecurrence', recurrenceSummaryText, false, t),
    reviewRow(
      'rev-exclusion',
      'task.group.wizard.summaryExclusion',
      exclusionSummaryText,
      exclusionSummaryText === none,
      t,
      true,
    ),
    reviewRow(
      'rev-require-photo',
      'task.group.wizard.summaryRequirePhoto',
      schedule.requirePhoto
        ? t('task.group.wizard.summaryYes')
        : t('task.group.wizard.summaryNo'),
      false,
      t,
    ),
    reviewRow(
      'rev-notify',
      'task.inventory.wizard.summaryNotify',
      schedule.notifyEmployer
        ? t('task.inventory.wizard.summaryNotifyOn')
        : t('task.inventory.wizard.summaryNotifyOff'),
      false,
      t,
    ),
    reviewRow(
      'rev-remind-helper',
      'task.form.remindHelperBeforeDueLabel',
      schedule.remindHelperBeforeDue
        ? t('task.group.wizard.summaryYes')
        : t('task.group.wizard.summaryNo'),
      false,
      t,
    ),
  ];
};
